import moment from 'moment'
import * as React from 'react'
import styled from 'styled-components'
import { Looks3, LooksOne, LooksTwo } from 'styled-icons/material'
import { GlobalContext } from '..//frontend-lib//globalContext'
import { Avatar } from '../frontend-lib/components/common/Avatar'
import { Button } from '../frontend-lib/components/common/Button'
import { PersonImage } from '../frontend-lib/components/common/Image/PersonImage'
import { StoreImage } from '../frontend-lib/components/common/Image/StoreImage'
import { Select } from '../frontend-lib/components/common/Select'
import { ConfirmReservation } from '../frontend-lib/components/reservation/ConfirmReservation'
import { EditReservation } from '../frontend-lib/components/reservation/EditReservation'
import { RegistReservation } from '../frontend-lib/components/reservation/RegistReservation'
import { CancelReservation } from '../frontend-lib/components/reservation/CancelReservation'
import { Reserve } from '../frontend-lib/components/reservation/Reserve'
import { Modal, useReservationState } from '../frontend-lib/components/reservation/useReservationState'
import { getDayOfWeekLocaled, DayOfWeek } from '../frontend-lib/constants/dayOfWeek'
import { loadCatalogue } from '../frontend-lib/i18n'
import { I18nCatalogue } from '../frontend-lib/i18n/reservation/types'
import { routeFactories } from '../frontend-lib/routes'
import { fetchHairStylist, HairStylist } from '../frontend-lib/types/hairStylist'
import { fetchMenus, Menu } from '../frontend-lib/types/menu'
import { fetchReservedSpots, ReservedSpot, ReservedSpots } from '../frontend-lib/types/reservation'
import { fetchSalon, Salon } from '../frontend-lib/types/salon'
import { fetchReserveFee } from '../frontend-lib/types/serviceConfig'
import { KamismaPageContext } from './_app'
import ErrorComponent from './_error'
import { UserAgentType } from '../frontend-lib/detectUserAgent'
import { devices } from '../frontend-lib/styleConstants'
import Container from '../frontend-lib/components/common/Container'
import { ReservationContents } from '../frontend-lib/components/reservation/StylistInfo'
import Section from '../frontend-lib/components/common/Section'

interface Props {
  readonly catalogue: I18nCatalogue
  readonly stylist: HairStylist
  readonly menus: Menu[]
  readonly reservedSpots: ReservedSpots
  readonly statusCode?: number
  readonly isRegister: boolean
  readonly adminSalons: Salon[]
  readonly fee: number
}

interface State {
  readonly selectedSalon: Salon | null
  readonly selectedStylist: HairStylist | null
  readonly stylistOption: { value: string; label: string }[] | null
  readonly reservedSpots: ReservedSpots
  readonly dateList: moment.Moment[]
  readonly targetMonth: number
}

const ReservationStartTime = 9
const ReservationEndTime = 22
const ReserveTimeFormat = 'YYYY/MM/DD HH:mm'
const DateFormat = 'YYYY/MM/DD'

const reservation = ({ catalogue, statusCode, stylist, reservedSpots, isRegister, adminSalons, fee, menus }: Props) => {
  if (statusCode) {
    return <ErrorComponent statusCode={statusCode}></ErrorComponent>
  }

  // 所持しているメニューから特定メニュー名称(ID/名前)を取得
  const getSpecialSpots = (menus: Menu[]) => {
    const dupli_special_menu =
      menus.length > 0
        ? menus
            .filter(menu => menu.special_spot)
            .map(menu => {
              return { value: menu.special_spot.id, label: menu.special_spot.name }
            })
        : []
    dupli_special_menu.unshift({ value: '', label: catalogue.specialSpotLabel })
    const special_spots = dupli_special_menu.filter((item, index, array) => {
      return array.findIndex(item2 => item.value === item2.value) === index
    })
    return special_spots
  }

  const { me, detectedUserAgent } = React.useContext(GlobalContext)
  const isSalonAdmin = adminSalons.length != 0
  const calendarContainerRef = React.useRef<HTMLDivElement>(null)
  const reservationState = useReservationState()
  const duration = React.useMemo(() => (detectedUserAgent === UserAgentType.Desktop ? 14 : 5), [detectedUserAgent])
  const today = moment().startOf('day')

  const [state, setState] = React.useState<State>({
    selectedSalon: isSalonAdmin ? adminSalons[0] : null,
    selectedStylist: isSalonAdmin ? adminSalons[0].stylists[0] : null,
    stylistOption: isSalonAdmin ? getStylistOption(adminSalons[0].stylists) : null,
    reservedSpots: reservedSpots,
    dateList: createDateList(duration, today.clone().subtract(Math.floor(duration / 2) - 1, 'days')),
    targetMonth: 0,
  })

  let calendar = createCalendarElement(state.dateList)

  const salonOption = isSalonAdmin
    ? adminSalons.map(salon => {
        return {
          label: salon.name,
          value: salon.id,
        }
      })
    : null

  React.useEffect(() => {
    if (isSalonAdmin) {
      adminSalons[0].stylists[0].salons.push(adminSalons[0])
    }
  }, [])

  return (
    <Section>
      <Container>
        <SelectContainer>
          {salonOption && state.selectedSalon && (
            <SelectItemContainer>
              <SelectTitle>{catalogue.salon}</SelectTitle>
              <SelectSalon
                options={salonOption}
                defaultValue={state.selectedSalon.id}
                style={{ marginBottom: '12px' }}
                onChange={selectSalonChange}
              />
              {state.selectedSalon.logoImage && <Avatar size={'small'} src={state.selectedSalon.logoImage} alt="" />}
              {!state.selectedSalon.logoImage && <StoreImage size={192} style={{ border: 'solid 1px' }} />}
            </SelectItemContainer>
          )}
          {state.stylistOption && state.selectedStylist && (
            <SelectItemContainer>
              <SelectTitle>{catalogue.stylist}</SelectTitle>
              <SelectStylist
                options={state.stylistOption}
                defaultValue={state.selectedStylist.id}
                style={{ marginBottom: '12px' }}
                onChange={selectStylistChange}
              />
              {state.selectedStylist.image && <Avatar size={'small'} src={state.selectedStylist.image} alt="" />}
              {!state.selectedStylist.image && <PersonImage size={192} style={{ border: 'solid 1px' }} />}
            </SelectItemContainer>
          )}
        </SelectContainer>
        {!isRegister && <ReservationContents stylist={stylist} catalogue={catalogue} menus={menus} />}
        {!isRegister && (
          <CalendarPolicy>
            <p>
              <span style={{ color: 'green' }}>■</span>
              {catalogue.calendarAllowText}
            </p>
            <p>
              <span style={{ color: 'red' }}>■</span>
              {catalogue.calendarDenyText}
            </p>
          </CalendarPolicy>
        )}
        <SelectDateTimeTitle>{catalogue.selectDateTime}</SelectDateTimeTitle>
        {detectedUserAgent === UserAgentType.Desktop || isSalonAdmin
          ? createTermSwithButtonContainer()
          : createTermSwithForMobileButtonContainer()}
        <CalendarContainer ref={calendarContainerRef} style={{ marginTop: '24px' }}>
          {calendar}
        </CalendarContainer>
        {detectedUserAgent === UserAgentType.Desktop || isSalonAdmin
          ? createTermSwithButtonContainer()
          : createTermSwithForMobileButtonContainer()}
        {reservationState.modal === Modal.Regist && (
          <RegistReservation
            catalogue={catalogue}
            pageState={reservationState}
            stylist={isSalonAdmin ? state.selectedStylist! : stylist}
            special_spots={isSalonAdmin ? getSpecialSpots(state.selectedStylist!.menus) : getSpecialSpots(menus)}
            menus={isSalonAdmin ? state.selectedStylist!.menus : menus}
            onCreated={updateShowReservedSpots}></RegistReservation>
        )}
        {reservationState.modal === Modal.Confirm && (
          <ConfirmReservation
            catalogue={catalogue}
            pageState={reservationState}
            stylist={isSalonAdmin ? state.selectedStylist! : stylist}
            onEdited={updateShowReservedSpots}
          />
        )}
        {reservationState.modal === Modal.Edit && (
          <EditReservation
            catalogue={catalogue}
            pageState={reservationState}
            stylist={isSalonAdmin ? state.selectedStylist! : stylist}
            special_spots={isSalonAdmin ? getSpecialSpots(state.selectedStylist!.menus) : getSpecialSpots(menus)}
            menus={isSalonAdmin ? state.selectedStylist!.menus : menus}
            onEdited={updateShowReservedSpots}
          />
        )}
        {reservationState.modal === Modal.Reserve && (
          <Reserve
            catalogue={catalogue}
            pageState={reservationState}
            stylist={stylist}
            menus={menus}
            fee={fee}
            onReserved={updateShowReservedSpots}
          />
        )}
        {reservationState.modal === Modal.Cancel && (
          <CancelReservation
            catalogue={catalogue}
            pageState={reservationState}
            stylist={stylist}
            onEdited={updateShowReservedSpots}
          />
        )}
      </Container>
    </Section>
  )

  function getStylistOption(stylists: HairStylist[]) {
    let stylistOption: { label: string; value: string }[] = []
    stylists.forEach(stylist => {
      if (stylist.showFlag) {
        stylistOption.push({
          label: stylist.name,
          value: stylist.id.toString(),
        })
      }
    })

    return stylistOption
  }

  function createTermSwithButtonContainer() {
    return (
      <TermSwitchButtonContainer>
        {state.dateList[0].isAfter(today) && (
          <TermSwitchButton look="white" style={{ float: 'left' }} onClick={() => termSwitchButtonClick(false)}>
            ＜ {catalogue.prev}
          </TermSwitchButton>
        )}
        <TermSwitchButton look="white" style={{ float: 'right' }} onClick={() => termSwitchButtonClick(true)}>
          {catalogue.after} ＞
        </TermSwitchButton>
      </TermSwitchButtonContainer>
    )
  }

  function createTermSwithForMobileButtonContainer() {
    return (
      <TermSwitchButtonContainer>
        {state.targetMonth != 0 && (
          <TermSwitchButton
            look="white"
            style={{ float: 'left' }}
            onClick={() => termSwitchForMoibleButtonClick(false)}>
            ＜ {catalogue.prevMonth}
          </TermSwitchButton>
        )}
        <TermSwitchButton look="white" style={{ float: 'right' }} onClick={() => termSwitchForMoibleButtonClick(true)}>
          {catalogue.afterMonth} ＞
        </TermSwitchButton>
        <div style={{ textAlign: 'center' }}>
          {moment()
            .add('months', state.targetMonth)
            .format('MM')}
          {catalogue.thisMonth}
        </div>
      </TermSwitchButtonContainer>
    )
  }

  function createCalendarElement(dateList: moment.Moment[]) {
    const tableHeaderElement =
      detectedUserAgent === UserAgentType.Desktop || isSalonAdmin
        ? createTableHeaderElement(dateList)
        : createTableHeaderForMoblieElement()
    const tableBodyElement =
      detectedUserAgent === UserAgentType.Desktop || isSalonAdmin
        ? createTableBodyElement()
        : createTableBodyForMobileElement()
    return (
      <>
        <table cellPadding={0} cellSpacing={0} style={{ marginBottom: '12px', width: '100%', background: '#FAFAFA' }}>
          <tbody>
            {tableHeaderElement}
            {tableBodyElement}
          </tbody>
        </table>
        <style>{`table, td, th {
            border: 1px solid #CCCCCC;
            border-collapse: collapse;
            font-size: 18px;
            text-align: center;
        }`}</style>
      </>
    )
  }

  function createTableHeaderElement(dateList: moment.Moment[]) {
    let month = { first: 0, second: 0 }
    let isFirst = true
    let currentMonth = -1

    dateList.map(date => {
      if (currentMonth == -1) {
        currentMonth = date.month()
      } else if (currentMonth != date.month()) {
        currentMonth = date.month()
        isFirst = false
      }

      if (isFirst) {
        month['first']++
      } else {
        month['second']++
      }
    })

    const dateListElement = dateList.map((date, index) => {
      return (
        <>
          {date.day() != DayOfWeek.Saturday && date.day() != DayOfWeek.Sunday && (
            <DispDateCell key={index}>
              <span style={{ fontWeight: 'bold' }}>{date.date()}</span>
              <div>{getDayOfWeekLocaled(catalogue, date.day())}</div>
            </DispDateCell>
          )}
          {date.day() == DayOfWeek.Sunday && (
            <DispSundayCell key={index}>
              <span style={{ fontWeight: 'bold' }}>{date.date()}</span>
              <div>{getDayOfWeekLocaled(catalogue, date.day())}</div>
            </DispSundayCell>
          )}
          {date.day() == DayOfWeek.Saturday && (
            <DispSaturdayCell key={index}>
              <span style={{ fontWeight: 'bold' }}>{date.date()}</span>
              <div>{getDayOfWeekLocaled(catalogue, date.day())}</div>
            </DispSaturdayCell>
          )}
        </>
      )
    })

    return (
      <>
        <tr>
          <th></th>
          <MonthCell colSpan={month.first}>{dateList[0].format('YYYY/M')}</MonthCell>
          {month['second'] > 0 && (
            <MonthCell colSpan={month.second}>{dateList.slice(-1)[0].format('YYYY/M')}</MonthCell>
          )}
          <th></th>
        </tr>
        <tr>
          <td></td>
          {dateListElement}
          <td></td>
        </tr>
      </>
    )
  }

  function createTableHeaderForMoblieElement() {
    return (
      <>
        <tr>
          <th>{catalogue.reservationDayOfWeek.sun}</th>
          <th>{catalogue.reservationDayOfWeek.mon}</th>
          <th>{catalogue.reservationDayOfWeek.tue}</th>
          <th>{catalogue.reservationDayOfWeek.wed}</th>
          <th>{catalogue.reservationDayOfWeek.thu}</th>
          <th>{catalogue.reservationDayOfWeek.fri}</th>
          <th>{catalogue.reservationDayOfWeek.sat}</th>
        </tr>
      </>
    )
  }

  function createTableBodyElement() {
    let timeElement = []

    for (let i = ReservationStartTime; i < ReservationEndTime; i++) {
      timeElement.push(
        <React.Fragment key={i}>
          <tr>
            <TimeCell style={{ fontWeight: 'bold' }}>{i}:00</TimeCell>
            {createReservationList(i + ':00')}
            <TimeCell style={{ fontWeight: 'bold' }}>{i}:00</TimeCell>
          </tr>
          <tr>
            <TimeCell>{i}:30</TimeCell>
            {createReservationList(i + ':30')}
            <TimeCell>{i}:30</TimeCell>
          </tr>
        </React.Fragment>,
      )
    }

    return timeElement
  }

  function createTableBodyForMobileElement() {
    let timeMobileElement: JSX.Element[] = []

    const startOfMonth = moment()
      .add('months', state.targetMonth)
      .startOf('month')
    const endOfMonth = parseInt(
      moment()
        .add('months', state.targetMonth)
        .endOf('month')
        .format('D'),
    )
    const startOfMonthDay = parseInt(startOfMonth.format('d'))
    let splitDay = [0]

    for (let i = 0; i < 6; i++) {
      if (7 - startOfMonthDay + 7 * i < endOfMonth) {
        splitDay.push(7 - startOfMonthDay + 7 * i)
      }
    }

    splitDay.map(i =>
      timeMobileElement.push(
        <React.Fragment key={i}>
          <tr>
            <DateCell key={i.toString() + '-0'}>
              {(i == 0 && startOfMonthDay != 0) || (i !== 0 && i + 1 > endOfMonth)
                ? ''
                : createReservationForMobile(
                    (i += 1),
                    startOfMonth.format('YYYY') + '-' + startOfMonth.format('MM') + '-' + i,
                  )}
            </DateCell>
            <DateCell key={i.toString() + '-1'}>
              {(i == 0 && startOfMonthDay - 1 > 0) || (i !== 0 && i + 1 > endOfMonth)
                ? ''
                : createReservationForMobile(
                    (i += 1),
                    startOfMonth.format('YYYY') + '-' + startOfMonth.format('MM') + '-' + i,
                  )}
            </DateCell>
            <DateCell key={i.toString() + '-2'}>
              {(i == 0 && startOfMonthDay - 2 > 0) || (i !== 0 && i + 1 > endOfMonth)
                ? ''
                : createReservationForMobile(
                    (i += 1),
                    startOfMonth.format('YYYY') + '-' + startOfMonth.format('MM') + '-' + i,
                  )}
            </DateCell>
            <DateCell key={i.toString() + '-3'}>
              {(i == 0 && startOfMonthDay - 3 > 0) || (i !== 0 && i + 1 > endOfMonth)
                ? ''
                : createReservationForMobile(
                    (i += 1),
                    startOfMonth.format('YYYY') + '-' + startOfMonth.format('MM') + '-' + i,
                  )}
            </DateCell>
            <DateCell key={i.toString() + '-4'}>
              {(i == 0 && startOfMonthDay - 4 > 0) || (i !== 0 && i + 1 > endOfMonth)
                ? ''
                : createReservationForMobile(
                    (i += 1),
                    startOfMonth.format('YYYY') + '-' + startOfMonth.format('MM') + '-' + i,
                  )}
            </DateCell>
            <DateCell key={i.toString() + '-5'}>
              {(i == 0 && startOfMonthDay - 5 > 0) || (i !== 0 && i + 1 > endOfMonth)
                ? ''
                : createReservationForMobile(
                    (i += 1),
                    startOfMonth.format('YYYY') + '-' + startOfMonth.format('MM') + '-' + i,
                  )}
            </DateCell>
            <DateCell key={i.toString() + '-6'}>
              {(i == 0 && startOfMonthDay - 6 > 0) || (i !== 0 && i + 1 > endOfMonth)
                ? ''
                : createReservationForMobile(
                    (i += 1),
                    startOfMonth.format('YYYY') + '-' + startOfMonth.format('MM') + '-' + i,
                  )}
            </DateCell>
          </tr>
        </React.Fragment>,
      ),
    )

    return timeMobileElement
  }

  function createReservationForMobile(wd: number, datetime: string) {
    let reservedSpots = state.reservedSpots

    if (isSalonAdmin) {
      /**set only admin salons spots */
      let salonAdminReserveSpotList: ReservedSpots = []
      reservedSpots.forEach(spot => {
        if (spot.salonId == state.selectedSalon!.id.toString()) {
          salonAdminReserveSpotList.push(spot)
        }
      })
      reservedSpots = salonAdminReserveSpotList
    }

    const date = moment(datetime, ReserveTimeFormat)
    let hasSpot = false
    let isReserve = false
    let spots = 0
    let spotId = ''
    let salonId = ''
    let reserveCount = 0
    let deleteFlag = false
    let targetDateSpots: any[] = []

    /**Check reservation spot. */
    reservedSpots.forEach(spot => {
      const momentObj = moment(spot.dateTime, ReserveTimeFormat)
      if (momentObj.year() == date.year() && momentObj.month() == date.month() && momentObj.date() == date.date()) {
        hasSpot = true
        isReserve = spot.spots > 0 ? true : false
        spots = spot.spots
        spotId = spot.id
        salonId = spot.salonId
        reserveCount = spot.reserves.length
        deleteFlag = spot.deleteFlag
        targetDateSpots.push(spot)
      }
    })

    return isRegister ? (
      <DateCellButton
        look={reserveCount > 0 ? 'primary' : 'white'}
        style={{ backgroundColor: deleteFlag ? 'red' : '', pointerEvents: deleteFlag ? 'none' : 'auto' }}
        onClick={() => (deleteFlag ? undefined : calendarForMobileButtonClick(spotId, targetDateSpots))}>
        {hasSpot && (
          <>
            <img src={getSalonLogoById(salonId)} width={48} height={48} />
            {spots == 1 && <LooksOne size={24} />}
            {spots == 2 && <LooksTwo size={24} />}
            {spots == 3 && <Looks3 size={24} />}
          </>
        )}
        {wd}
      </DateCellButton>
    ) : hasSpot && date.isSameOrAfter(today) ? (
      <DateCellButton
        look="white"
        style={{ backgroundColor: isReserve ? 'green' : 'red', pointerEvents: isReserve ? 'auto' : 'none' }}
        onClick={() => (isReserve ? calendarForMobileButtonClick(spotId, targetDateSpots) : undefined)}>
        {wd}
      </DateCellButton>
    ) : (
      <>{wd}</>
    )
  }

  function createReservationList(time: string) {
    const reservationList: JSX.Element[] = []
    let reservedSpots = state.reservedSpots

    if (isSalonAdmin) {
      /**set only admin salons spots */
      let salonAdminReserveSpotList: ReservedSpots = []
      reservedSpots.forEach(spot => {
        if (spot.salonId == state.selectedSalon!.id.toString()) {
          salonAdminReserveSpotList.push(spot)
        }
      })
      reservedSpots = salonAdminReserveSpotList
    }

    for (let i = 0; i < duration; i++) {
      const date = state.dateList[i]
      let hasSpot = false
      let isReserve = false
      let spots = 0
      let spotId = ''
      let salonId = ''
      let reserveCount = 0
      let deleteFlag = false
      /**Check reservation spot. */
      reservedSpots.forEach(spot => {
        const momentObj = moment(spot.dateTime, ReserveTimeFormat)
        const minute = momentObj.minute().toString() == '0' ? '00' : momentObj.minute().toString()
        if (
          momentObj.year() == date.year() &&
          momentObj.month() == date.month() &&
          momentObj.date() == date.date() &&
          momentObj.hour().toString() == time.split(':')[0] &&
          minute == time.split(':')[1]
        ) {
          hasSpot = true
          isReserve = spot.spots > 0 ? true : false
          spots = spot.spots
          spotId = spot.id
          salonId = spot.salonId
          reserveCount = spot.reserves.length
          deleteFlag = spot.deleteFlag
        }
      })

      reservationList.push(
        <DateCell key={i.toString() + '-' + time}>
          {isRegister ? (
            <DateCellButton
              look={reserveCount > 0 ? 'primary' : 'white'}
              style={{ backgroundColor: deleteFlag ? 'red' : '', pointerEvents: deleteFlag ? 'none' : 'auto' }}
              onClick={() => (deleteFlag ? undefined : calendarButtonClick(spotId, i, time))}>
              {hasSpot && (
                <>
                  <img src={getSalonLogoById(salonId)} width={48} height={48} />
                  {spots == 1 && <LooksOne size={24} />}
                  {spots == 2 && <LooksTwo size={24} />}
                  {spots == 3 && <Looks3 size={24} />}
                </>
              )}
            </DateCellButton>
          ) : hasSpot && date.isSameOrAfter(today) ? (
            <DateCellButton
              look="white"
              style={{ backgroundColor: isReserve ? 'green' : 'red', pointerEvents: isReserve ? 'auto' : 'none' }}
              onClick={() => (isReserve ? calendarButtonClick(spotId, i, time) : undefined)}></DateCellButton>
          ) : null}
        </DateCell>,
      )
      hasSpot = false
    }
    return reservationList
  }

  function calendarButtonClick(spotId: string, dateIndex: number, time: string) {
    /** redirect sign-in page. if user not sign in. */
    if (!me) {
      location.href = routeFactories.signIn.getRoute().as!
      return
    }

    if (isRegister) {
      /** regist reservation. if not exist reservation data. */
      if (spotId == '') {
        if (menus.length < 1) {
          alert(catalogue.regist.nothingMenuError)
          return
        }

        /** regist reservation. */
        reservationState.setState({
          modal: Modal.Regist,
          selectedTime: state.dateList[dateIndex].format(DateFormat) + ' ' + time,
          reservedSpot: null,
          cancelReservation: null,
          spots: [],
          isMobile: false,
        })
      } else {
        /** edit reservation. if exist reservation data. */
        const spot = getReservedSpotById(spotId)
        reservationState.setState({
          modal: spot!.reserves && spot!.reserves.length > 0 ? Modal.Confirm : Modal.Edit,
          selectedTime: state.dateList[dateIndex].format(DateFormat) + ' ' + time,
          reservedSpot: spot,
          cancelReservation: null,
          spots: [],
          isMobile: false,
        })
      }
    } else {
      /** confitm reservation. if exist reservation data. */
      if (spotId != '') {
        /** show reservation dialog. */
        reservationState.setState({
          modal: Modal.Reserve,
          selectedTime: state.dateList[dateIndex].format(DateFormat) + ' ' + time,
          reservedSpot: getReservedSpotById(spotId),
          cancelReservation: null,
          spots: [],
          isMobile: false,
        })
      }
    }
  }

  function calendarForMobileButtonClick(spotId: string, spots: any[]) {
    /** redirect sign-in page. if user not sign in. */
    if (!me) {
      location.href = routeFactories.signIn.getRoute().as!
      return
    }
    /** confitm reservation. if exist reservation data. */

    /** show reservation dialog. */
    reservationState.setState({
      modal: Modal.Reserve,
      selectedTime: '',
      reservedSpot: getReservedSpotById(spotId),
      cancelReservation: null,
      spots: spots,
      isMobile: true,
    })
  }

  function termSwitchButtonClick(isNext: boolean) {
    const momentObj = isNext
      ? state.dateList[0].add({ days: duration })
      : state.dateList[0].subtract({ days: duration })

    let dateList = createDateList(duration, momentObj)
    setState({
      selectedSalon: state.selectedSalon,
      selectedStylist: state.selectedStylist,
      stylistOption: state.stylistOption,
      reservedSpots: state.reservedSpots,
      dateList: dateList,
      targetMonth: state.targetMonth,
    })
  }

  function termSwitchForMoibleButtonClick(isNext: boolean) {
    setState({
      selectedSalon: state.selectedSalon,
      selectedStylist: state.selectedStylist,
      stylistOption: state.stylistOption,
      reservedSpots: state.reservedSpots,
      dateList: state.dateList,
      targetMonth: isNext ? state.targetMonth + 1 : state.targetMonth - 1,
    })
  }

  async function selectSalonChange(event: React.ChangeEvent<HTMLSelectElement>) {
    const salonId = event.target.value
    for (let salon of adminSalons) {
      if (salonId == salon.id.toString()) {
        const reservedSpots = await fetchReservedSpots(salon.stylists[0].id.toString(), salon.id.toString(), true)
        salon.stylists[0].salons.push(salon)
        setState({
          selectedSalon: salon,
          selectedStylist: salon.stylists[0],
          stylistOption: getStylistOption(salon.stylists),
          reservedSpots: reservedSpots ? reservedSpots : new Array(0),
          dateList: state.dateList,
          targetMonth: state.targetMonth,
        })
      }
    }
  }

  async function selectStylistChange(event: React.ChangeEvent<HTMLSelectElement>) {
    const stylistId = event.target.value
    if (!state.selectedSalon) {
      return
    }

    for (let stylist of state.selectedSalon.stylists) {
      if (stylistId == stylist.id.toString()) {
        const reservedSpots = await fetchReservedSpots(stylist!.id.toString(), state.selectedSalon.id.toString(), true)
        stylist.salons.push(state.selectedSalon)
        setState({
          selectedSalon: state.selectedSalon,
          selectedStylist: stylist,
          stylistOption: getStylistOption(state.selectedSalon!.stylists),
          reservedSpots: reservedSpots ? reservedSpots : new Array(0),
          dateList: state.dateList,
          targetMonth: state.targetMonth,
        })
      }
    }
  }

  function getReservedSpotById(reservedSpotId: string): ReservedSpot | null {
    for (let i = 0; i < state.reservedSpots.length; i++) {
      const spot = state.reservedSpots[i]
      if (spot.id === reservedSpotId) {
        return spot
      }
    }

    return null
  }

  function getSalonLogoById(salonId: string) {
    let logoImage = ''
    stylist.salons.map(salon => {
      if (salon.id.toString() == salonId) {
        logoImage = salon.logoImage
      }
    })
    return logoImage
  }

  function createDateList(duration: number, startDate: moment.Moment): moment.Moment[] {
    let dateList: moment.Moment[] = []

    for (let i = 0; i < duration; i++) {
      dateList.push(moment(startDate))
      startDate.add({ days: 1 })
    }
    return dateList
  }

  async function updateShowReservedSpots() {
    /** call fetch reserved spots*/
    let target = isSalonAdmin ? state.selectedStylist! : stylist
    let reservedSpots = await getReservedSpots(target, isRegister)
    setState({
      selectedSalon: isSalonAdmin ? state.selectedSalon : null,
      selectedStylist: isSalonAdmin ? state.selectedStylist : null,
      stylistOption: isSalonAdmin ? getStylistOption(state.selectedSalon!.stylists) : null,
      reservedSpots: reservedSpots,
      dateList: state.dateList,
      targetMonth: state.targetMonth,
    })
  }
}

reservation.getInitialProps = async ({ req, me, query }: KamismaPageContext) => {
  const catalogue = await loadCatalogue<I18nCatalogue>('reservation', me, req)

  if (!query.id) {
    return { catalogue, statusCode: 404 }
  }

  const stylist = await fetchHairStylist(query.id.toString(), req)
  if (stylist === null) {
    return { catalogue, statusCode: 404 }
  }

  const menus = await fetchMenus(query.id.toString(), req)
  if (menus === null) {
    return { catalogue, statusCode: 404 }
  }

  const fee = await fetchReserveFee(req)
  if (fee == null) {
    return { catalogue, statusCode: 404 }
  }

  /**
   * check role. salon admin and stylist can register reservation.
   * common user can reserve.
   */

  let isRegister = false
  let adminSalons: Salon[] = []

  for (let salon of stylist!.salons) {
    const resSalon = await fetchSalon(salon.id.toString())
    if (resSalon) {
      /** check role and add to admin salons*/
      setAdminSalons(resSalon)
    }
  }

  if (me && me.hairStylist) {
    if (me.hairStylist.id == stylist.id) {
      isRegister = true
    } else {
      isRegister = adminSalons.length != 0
    }
  }

  const reservedSpots: ReservedSpots = await getReservedSpots(stylist, isRegister)

  return { catalogue, stylist, reservedSpots, isRegister, adminSalons, fee, menus }

  function setAdminSalons(salon: Salon) {
    for (let stylist of salon.stylists) {
      if (me && me.hairStylist && stylist.id == me.hairStylist.id && stylist.role == 'admin') {
        adminSalons.push(salon)
      }
    }
  }
}

export default reservation

async function getReservedSpots(stylist: HairStylist, isRegister: boolean) {
  let result: ReservedSpots = []

  for (let salon of stylist!.salons) {
    /** all fetch data if current user is stylist or salon owner. */
    let spots = await fetchReservedSpots(stylist!.id.toString(), salon.id.toString(), isRegister)
    if (spots != null) {
      spots.forEach((spot: any) => {
        result.push(spot)
      })
    }
  }

  return result
}

const SelectDateTimeTitle = styled.h2`
  margin-top: 48px;
  margin-bottom: 48px;
  font-size: 20px;
  font-weight: normal;
  border-block-end: solid 1px #5a5129;
  padding-bottom: 12px;
  color: #5a5129;
`

const TermSwitchButtonContainer = styled.div`
  color: #5a5129;
  margin-top: 12px;
  margin-bottom: 12px;
  display: flow-root;

  @media ${devices.mobile} {
    margin-top: 0px;
    margin-bottom: 0px;
  }
`

const TermSwitchButton = styled(Button)`
  @media ${devices.mobile} {
    font-size: 12px;
  }
`

const CalendarContainer = styled.div``

const MonthCell = styled.th`
  padding: 0.5em 0;
  font-weight: bold;

  @media ${devices.mobile} {
    font-size: 12px;
  }
`

const DateCell = styled.td`
  width: 6%;
  height: 63px;

  @media ${devices.mobile} {
    width: 14%;
    height: 45px;
    font-size: 12px;
  }
`

const DispDateCell = styled(DateCell)`
  background: #f3f2eb;
`

const DispSundayCell = styled(DateCell)`
  color: #c6223d;
  background: rgba(255, 245, 238, 0.68);
`

const DispSaturdayCell = styled(DateCell)`
  color: #4285f4;
  background: #f0f8ff;
`

const DateCellButton = styled(Button)`
  display: block;
  padding: 4px 0;
  width: 100%;
  height: 100%;
  text-align: center;
`

const TimeCell = styled.td`
  @media ${devices.mobile} {
    width: 15%;
    font-size: 12px;
  }
`

const SelectContainer = styled.div`
  text-align: center;
`

const SelectItemContainer = styled.div`
  margin: 12px;
  display: inline-grid;
  text-align: center;
`

const SelectStylist = styled(Select)`
  width: 192px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
`

const SelectSalon = SelectStylist

const SelectTitle = styled.div`
  font-size: 24px;
  font-weight: bold;
  margin: 12px;
`

const CalendarPolicy = styled.div`
  font-size: 18px;

  @media ${devices.mobile} {
    font-size: 12px;
  }
`
